import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      setStatus('error');
      setStatusMessage('Message must be 500 characters or less');
      return;
    }

    setStatus('sending');
    setStatusMessage('Sending your message...');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5201mpn1516q9jm3tm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setStatus('success');
        setStatusMessage('Message sent successfully! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => {
          setStatus('idle');
          setStatusMessage('');
        }, 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setStatus('error');
      setStatusMessage('Failed to send message. Please try again.');
      setTimeout(() => {
        setStatus('idle');
        setStatusMessage('');
      }, 5000);
    }
  };

  return (
    <section id="contact" className="relative">
      <div className="grid grid-cols-2">
        <div className="bg-[#0A1628] px-16 py-24 flex items-center">
          <div className="w-full">
            <h2 className="text-5xl font-bold text-white mb-5">Get In Touch</h2>
            <p className="text-[#7DD3FC] text-lg mb-12">We're here to help with your VPN needs</p>

            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-mail-line text-[#00D9FF] text-xl"></i>
                  <span className="text-[#B8C5D6] text-xs uppercase tracking-wider whitespace-nowrap">EMAIL</span>
                </div>
                <a href="mailto:info@jndossmeloltda.com" className="text-white text-base hover:text-[#00D9FF] transition-colors whitespace-nowrap">
                  info@jndossmeloltda.com
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-phone-line text-[#00D9FF] text-xl"></i>
                  <span className="text-[#B8C5D6] text-xs uppercase tracking-wider whitespace-nowrap">PHONE</span>
                </div>
                <a href="tel:+15866660942" className="text-white text-base hover:text-[#00D9FF] transition-colors whitespace-nowrap">
                  +1 5866660942
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-map-pin-line text-[#00D9FF] text-xl"></i>
                  <span className="text-[#B8C5D6] text-xs uppercase tracking-wider whitespace-nowrap">ADDRESS</span>
                </div>
                <address className="text-white text-base not-italic leading-relaxed">
                  Rua BEM-TE-VIS 264<br />
                  SETOR W QUADRA14 LOTE 06<br />
                  IPIRANGA DO NORTE<br />
                  78578-000, Brazil
                </address>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white px-16 py-24 flex items-center">
          <div className="w-full max-w-xl">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-4 border border-[#E2E8F0] rounded-2xl text-base focus:outline-none focus:border-[#00D9FF] focus:ring-2 focus:ring-[#00D9FF]/20 transition-all"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-4 border border-[#E2E8F0] rounded-2xl text-base focus:outline-none focus:border-[#00D9FF] focus:ring-2 focus:ring-[#00D9FF]/20 transition-all"
                />
              </div>

              <div>
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-4 border border-[#E2E8F0] rounded-2xl text-base focus:outline-none focus:border-[#00D9FF] focus:ring-2 focus:ring-[#00D9FF]/20 transition-all"
                />
              </div>

              <div>
                <textarea
                  name="message"
                  placeholder="Your message..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-4 border border-[#E2E8F0] rounded-2xl text-base focus:outline-none focus:border-[#00D9FF] focus:ring-2 focus:ring-[#00D9FF]/20 transition-all resize-none"
                />
                <div className="text-right text-sm text-[#5A6B7F] mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              {statusMessage && (
                <div className={`p-4 rounded-xl ${
                  status === 'success' ? 'bg-green-50 text-green-700' :
                  status === 'error' ? 'bg-red-50 text-red-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  {statusMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-[#00D9FF] text-white py-4 rounded-2xl text-base font-bold hover:bg-[#00C4E6] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
